import { useState, useRef, useLayoutEffect, useCallback } from "react";

import { isSupSubNode, isEqualsSign } from "@/models/utils/semanticChecks";

import type { InlineNodeContentRenderItem } from "@/components/expression/inline-node/types";
import type { SupSubOperator } from "@/models/nodes/operators/types";
import type { NodeModel } from "@/models/nodes/types";

export function groupNodesWithAttachments(nodes: NodeModel[]): InlineNodeContentRenderItem[] {
  const result: [NodeModel | null, number, NodeModel[]][] = [];

  let i = 0;
  while (i < nodes.length) {
    let baseNode: NodeModel | null = null;
    const attachments: NodeModel[] = [];
    const startIndex = i;
    let currentNode = nodes[i];
    let firstAttachment: SupSubOperator | null = null;

    if (!isSupSubNode(currentNode)) {
      baseNode = currentNode;
      i += 1;
      currentNode = nodes[i];
    }

    if (isSupSubNode(currentNode)) {
      firstAttachment = currentNode;
      attachments.push(currentNode);
      i += 1;
      currentNode = nodes[i];

      if (isSupSubNode(currentNode) && firstAttachment?.operatorType !== currentNode.operatorType) {
        attachments.push(currentNode);
        i += 1;
      }
    }

    result.push([baseNode, startIndex, attachments]);
  }

  return result
    .filter(([n, , attachments]) => n || attachments.length > 0)
    .map(([n, index, attachmentNodes]) => ({
      key: n?.id ?? `orphan-attachment-${index}`,
      node: n,
      inlineIndex: index,
      attachmentNodes,
    }));
}

/**
 * Marks items up to and including the top-level `=` as `dimmed` when rendering
 * as a suggestion preview. Used to visually de-emphasise the formula so the
 * computed result reads as the primary output.
 */
export function applyComputedDimming(
  items: InlineNodeContentRenderItem[],
  computedSplit: boolean | undefined,
  renderContext: string | undefined,
): InlineNodeContentRenderItem[] {
  if (!computedSplit || renderContext !== "suggestion") return items;
  const eqIndex = items.findIndex((item) => !!item.node && isEqualsSign(item.node));
  if (eqIndex === -1) return items;
  return items.map((item, i) => (i <= eqIndex ? { ...item, dimmed: true } : item));
}

export const ITEM_GAP_PX = 1;

export function useItemWidths() {
  // getBoundingClientRect and ResizeObserver return fractional pixel dimensions
  // value chosen is small enough that no real content change would be missed,
  // but large enough to filter out floating-point noise
  const jitterThreshold = 0.5;
  const [widths, setWidths] = useState<Record<string, number>>({});
  const widthsRef = useRef(widths);

  useLayoutEffect(() => {
    widthsRef.current = widths;
  });

  const updateWidth = useCallback((key: string, width: number) => {
    const prevRef = widthsRef.current[key];
    if (prevRef !== undefined && Math.abs(prevRef - width) < jitterThreshold) return;

    setWidths((prev) => {
      if (prev[key] !== undefined && Math.abs(prev[key] - width) < jitterThreshold) return prev;
      return { ...prev, [key]: width };
    });
  }, []);

  return { widths, updateWidth };
}
